import {
  getSingaporeDateKeyFromInstant,
  getSingaporeMinutesOfDay,
  singaporeCalendarDayFromInstant,
} from "./timezone";

export const SITE_VISIT_SLOT_START_HOUR = 8;
export const SITE_VISIT_SLOT_END_HOUR = 20;
export const SITE_VISIT_SLOTS_PER_HOUR = 2;
export const SITE_VISIT_TOTAL_SLOTS =
  (SITE_VISIT_SLOT_END_HOUR - SITE_VISIT_SLOT_START_HOUR) * SITE_VISIT_SLOTS_PER_HOUR;

const SLOT_START_HOUR = SITE_VISIT_SLOT_START_HOUR;
const TOTAL_SLOTS = SITE_VISIT_TOTAL_SLOTS;

const LEGACY_WINDOW_TO_SLOT: Record<string, number> = {
  "9am-10am": 2,
  "10am-11am": 4,
  "11am-12pm": 6,
  "2pm-3pm": 12,
  "3pm-4pm": 14,
  "4pm-5pm": 16,
};

const LEGACY_WINDOW_END_SLOT: Record<string, number> = {
  "9am-10am": 3,
  "10am-11am": 5,
  "11am-12pm": 7,
  "2pm-3pm": 13,
  "3pm-4pm": 15,
  "4pm-5pm": 17,
};

export function siteVisitSlotLabel(slotIdx: number): string {
  const totalMins = SLOT_START_HOUR * 60 + slotIdx * 30;
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m === 0 ? "00" : "30"} ${ampm}`;
}

export function slotToWindow(slotIdx: number): string {
  const totalMins = SLOT_START_HOUR * 60 + slotIdx * 30;
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  const endTotalMins = totalMins + 30;
  const eh = Math.floor(endTotalMins / 60);
  const em = endTotalMins % 60;
  function fmt(hour: number, min: number): string {
    const ampm = hour < 12 ? "am" : "pm";
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return min === 0 ? `${h12}${ampm}` : `${h12}:${String(min).padStart(2, "0")}${ampm}`;
  }
  return `${fmt(h, m)}-${fmt(eh, em)}`;
}

export function windowToSlot(win: string): number {
  if (win in LEGACY_WINDOW_TO_SLOT) return LEGACY_WINDOW_TO_SLOT[win]!;
  const freeForm = win.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (freeForm) {
    const startH = Number(freeForm[1]);
    const startM = Number(freeForm[2]);
    const totalMins = startH * 60 + startM;
    const slotMins = SLOT_START_HOUR * 60;
    return Math.floor((totalMins - slotMins) / 30);
  }
  for (let i = 0; i < TOTAL_SLOTS; i++) {
    if (slotToWindow(i) === win) return i;
  }
  return -1;
}

export const SITE_VISIT_SLOT_WINDOWS: string[] = Array.from({ length: TOTAL_SLOTS }, (_, i) => slotToWindow(i));

export function buildArrivalWindowFromSlots(startSlot: number, endSlot: number): string | undefined {
  if (startSlot < 0 || endSlot < startSlot) return undefined;
  const startMins = SLOT_START_HOUR * 60 + startSlot * 30;
  const endMins = SLOT_START_HOUR * 60 + (endSlot + 1) * 30;
  const pad = (n: number) => String(n).padStart(2, "0");
  const sh = Math.floor(startMins / 60);
  const sm = startMins % 60;
  const eh = Math.floor(endMins / 60);
  const em = endMins % 60;
  return `${pad(sh)}:${pad(sm)}-${pad(eh)}:${pad(em)}`;
}

export function slotRangeFromArrivalWindow(win: string | null | undefined): { startSlot: number; endSlot: number } | null {
  const value = String(win ?? "").trim();
  if (!value) return null;
  const freeForm = value.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (freeForm) {
    const startMins = Number(freeForm[1]) * 60 + Number(freeForm[2]);
    const endMins = Number(freeForm[3]) * 60 + Number(freeForm[4]);
    const startSlot = Math.floor((startMins - SLOT_START_HOUR * 60) / 30);
    const endSlot = Math.max(startSlot, Math.ceil((endMins - SLOT_START_HOUR * 60) / 30) - 1);
    return { startSlot, endSlot };
  }
  if (value in LEGACY_WINDOW_TO_SLOT) {
    return { startSlot: LEGACY_WINDOW_TO_SLOT[value]!, endSlot: LEGACY_WINDOW_END_SLOT[value]! };
  }
  const startSlot = windowToSlot(value);
  if (startSlot < 0) return null;
  return { startSlot, endSlot: startSlot };
}

export function slotRangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart <= bEnd && aEnd >= bStart;
}

export type SiteVisitCalendarPlacement = {
  placementDate: Date;
  startMins: number;
  endMins: number;
  startSlot: number;
  endSlot: number;
};

function sgtMinutesFromUtcDate(value: Date): number {
  return getSingaporeMinutesOfDay(value) ?? 0;
}

export function resolveSiteVisitCalendarPlacement(visit: {
  visitDate: Date | string;
  arrivalWindow?: string | null;
  arrivedAt?: Date | string | null;
  departedAt?: Date | string | null;
}): SiteVisitCalendarPlacement | null {
  const START_MINS = SLOT_START_HOUR * 60;
  if (visit.arrivedAt) {
    const arrived = new Date(visit.arrivedAt);
    const startMins = sgtMinutesFromUtcDate(arrived);
    let endMins = startMins + 30;
    if (visit.departedAt) {
      endMins = sgtMinutesFromUtcDate(new Date(visit.departedAt));
    }
    if (endMins <= startMins) endMins = startMins + 30;
    const placementDate = singaporeCalendarDayFromInstant(arrived) ?? arrived;
    const startSlot = Math.max(0, Math.floor((startMins - START_MINS) / 30));
    const endSlot = Math.min(TOTAL_SLOTS - 1, Math.max(startSlot, Math.ceil((endMins - START_MINS) / 30) - 1));
    return { placementDate, startMins, endMins, startSlot, endSlot };
  }

  const range = slotRangeFromArrivalWindow(visit.arrivalWindow);
  if (!range) return null;
  const startMins = START_MINS + range.startSlot * 30;
  const endMins = START_MINS + (range.endSlot + 1) * 30;
  return {
    placementDate: singaporeCalendarDayFromInstant(visit.visitDate) ?? new Date(visit.visitDate),
    startMins,
    endMins,
    startSlot: range.startSlot,
    endSlot: range.endSlot,
  };
}

export function leavePeriodSlotRange(period: "full_day" | "am" | "pm"): { startSlot: number; endSlot: number } {
  const midpoint = TOTAL_SLOTS / 2;
  if (period === "am") return { startSlot: 0, endSlot: midpoint - 1 };
  if (period === "pm") return { startSlot: midpoint, endSlot: TOTAL_SLOTS - 1 };
  return { startSlot: 0, endSlot: TOTAL_SLOTS - 1 };
}
